import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { layoutNodeText, textPadding, textBox, textStyle, cssFont, colorToCss, colorToHex, parseColor, mul, connectorGeometry, connectorMidpoint, applyRunStyle, paragraphFormats, paragraphAt, hitTestText, caretPosition, selectionRects, getTextShaper, type NodeId, type TextRun, type TextLayout } from '@vellum/core';
import { app } from '../app.ts';
import { mindMapKey } from '../mindmap.ts';
import { textReview } from '../plugins.ts';
import { useApp } from '../hooks.ts';

/** A DOM textarea laid exactly over the node being edited. */
export function TextEditor({ id }: { id: NodeId }) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const range = useRef<[number, number]>([0, 0]);
  const [sel, setSel] = useState<[number, number]>([0, 0]);
  const anchor = useRef<number | null>(null);
  const [blink, setBlink] = useState(true);
  const node = useApp(['doc', 'camera'], () => app.doc.get(id));
  const [initial] = useState(() => app.doc.get(id)?.characters ?? '');
  // text review plugins: ask about the text a moment after typing stops
  const review = useApp('ui', () => textReview.results.get(id));
  const [suggest, setSuggest] = useState<{ start: number; end: number; options: string[]; x: number; y: number } | null>(null);
  const reviewTimer = useRef(0);
  const askReview = (text: string) => {
    clearTimeout(reviewTimer.current);
    reviewTimer.current = window.setTimeout(() => textReview.check(id, text), 350);
  };
  useEffect(() => {
    askReview(initial);
    return () => clearTimeout(reviewTimer.current);
  }, [id]);

  useLayoutEffect(() => {
    const ta = ref.current;
    if (!ta) return;
    ta.focus();
    if (initial) ta.select();
    range.current = [ta.selectionStart, ta.selectionEnd];
    setSel([ta.selectionStart, ta.selectionEnd]);
    // a layer added just now (text tool, sticky, shape) and its first text are one undo step
    app.editor.beginGesture('Edit text', initial ? {} : { continues: id });
    return () => {
      const n = app.doc.get(id);
      if (n && n.type === 'TEXT' && !(n.characters ?? '').trim()) app.editor.remove([id]);
      app.editor.endGesture();
    };
  }, [id, initial]);

  useLayoutEffect(() => {
    const t = setInterval(() => setBlink((b) => !b), 530);
    return () => clearInterval(t);
  }, []);

  if (!node) return null;
  const style = textStyle(node);
  const view = app.viewMatrix();
  const isText = node.type === 'TEXT';
  let m = mul(view, app.doc.worldTransform(id));
  let width: number | string = node.width;
  let height = node.height;
  let padTop = 0;
  const color = isText ? node.fills.find((f) => f.type === 'SOLID') : null;
  let textColor = color && color.type === 'SOLID' ? colorToCss(color.color, color.opacity ?? 1) : '#000';
  if (!isText) {
    textColor = node.textColor ? colorToCss(node.textColor) : '#1e1e1e';
    if (node.type === 'CONNECTOR') {
      const g = connectorGeometry(app.doc, node);
      const mid = connectorMidpoint(g.kind, g.points);
      const l = layoutNodeText({ ...node, type: 'TEXT', textAutoResize: 'WIDTH_AND_HEIGHT' });
      const w = Math.max(40, l.width + 8);
      m = mul(view, [1, 0, 0, 1, mid.x - w / 2, mid.y - style.lineHeight / 2]);
      width = w;
      height = Math.max(style.lineHeight, l.height);
    } else {
      const box = textBox(node);
      m = mul(m, [1, 0, 0, 1, box.x, box.y]);
      width = box.width;
      height = box.height;
      const layout = layoutNodeText(node);
      padTop = Math.max(0, layout.lines[0]?.y ?? 0);
    }
  }
  const autoWidth = isText && node.textAutoResize === 'WIDTH_AND_HEIGHT';
  // Rich text and lists: the canvas renders the text, and the caret and selection are drawn from
  // the same layout (a textarea can't indent single paragraphs or mix font sizes per line).
  const formats = paragraphFormats(node);
  const onPath = !!node.textPath && isText;
  // the textarea can't reproduce our tabular figures / ligature handling, so the canvas draws the text
  const features = !!node.openTypeFeatures;
  // with HarfBuzz shaping the canvas always draws the text (a textarea would shape it differently)
  const custom = !onPath && (!!getTextShaper() || !!node.textRuns?.length || !!formats || features);
  if (custom) textColor = 'transparent';
  const layout: TextLayout | null = custom && node.type !== 'CONNECTOR' ? layoutNodeText(node) : null;
  // text on a path: edit in a small floating field; the canvas shows the glyphs on the path
  if (onPath) {
    const b = app.doc.worldBounds(id);
    const top = app.pageToScreen({ x: b.x, y: b.y + b.height });
    m = [1, 0, 0, 1, top.x, top.y + 52]; // below the shape, leaving room for the format bar
    width = 260;
    textColor = '#1e1e1e';
  }
  const css: React.CSSProperties = {
    position: 'absolute',
    left: 0,
    top: 0,
    transformOrigin: '0 0',
    transform: `matrix(${m.join(',')})`,
    width: autoWidth ? Math.max(4, node.width + 2) : width,
    height: onPath ? 'auto' : Math.max(style.lineHeight, isText ? node.height : height),
    ...(onPath ? { font: '400 13px Inter, sans-serif', lineHeight: '18px', background: '#fff', border: '1px solid #0d99ff', borderRadius: 6, padding: '6px 8px', boxShadow: '0 4px 14px rgba(0,0,0,.15)', whiteSpace: 'pre-wrap' as const } : {}),
    paddingTop: padTop,
    font: cssFont(style),
    lineHeight: `${style.lineHeight}px`,
    letterSpacing: `${style.letterSpacing}px`,
    textAlign: style.textAlign === 'JUSTIFIED' ? 'justify' : (style.textAlign.toLowerCase() as 'left'),
    textTransform: style.textCase === 'UPPER' ? 'uppercase' : style.textCase === 'LOWER' ? 'lowercase' : style.textCase === 'TITLE' ? 'capitalize' : 'none',
    color: textColor,
    caretColor: layout ? 'transparent' : '#0d99ff',
    whiteSpace: autoWidth ? 'pre' : 'pre-wrap',
    overflowWrap: 'break-word',
  };
  const applyStyle = (props: Omit<TextRun, 'start' | 'end'>, toggleKey?: keyof Omit<TextRun, 'start' | 'end'>) => {
    const n = app.doc.get(id);
    if (!n) return;
    const len = (n.characters ?? '').length;
    let [a, b] = range.current;
    if (a === b) [a, b] = [0, len];
    // toggles: if the whole range already has the value, remove it
    if (toggleKey) {
      const has = (n.textRuns ?? []).some((r) => r.start <= a && r.end >= b && r[toggleKey] === props[toggleKey]);
      if (has) props = { [toggleKey]: undefined };
    }
    const runs = applyRunStyle(n.textRuns ?? [], len, a, b, props);
    app.editor.update(id, { textRuns: runs.length ? runs : undefined }, 'Format text');
    requestAnimationFrame(() => {
      ref.current?.focus();
      ref.current?.setSelectionRange(range.current[0], range.current[1]);
    });
  };
  const tl = { x: m[4], y: m[5] };
  const syncSel = () => {
    const t = ref.current;
    if (!t) return;
    range.current = [t.selectionStart, t.selectionEnd];
    setSel([t.selectionStart, t.selectionEnd]);
    setBlink(true);
  };
  const setRange = (a: number, b: number) => {
    const t = ref.current;
    if (!t) return;
    t.setSelectionRange(Math.min(a, b), Math.max(a, b), a > b ? 'backward' : 'forward');
    syncSel();
  };
  const localIndex = (e: React.PointerEvent) => (layout ? hitTestText(layout, e.nativeEvent.offsetX, e.nativeEvent.offsetY) : 0);
  /** Paragraph-level list editing; returns true when it handled the key. */
  const listKey = (e: React.KeyboardEvent<HTMLTextAreaElement>): boolean => {
    const n = app.doc.get(id);
    const t = e.currentTarget;
    if (!n) return false;
    const chars = t.value;
    const [a, b] = [t.selectionStart, t.selectionEnd];
    const fmts = paragraphFormats(n);
    const pi = paragraphAt(chars, a);
    const cur = fmts?.[pi];
    const paraStart = chars.lastIndexOf('\n', a - 1) + 1;
    const paraEnd = chars.indexOf('\n', a) < 0 ? chars.length : chars.indexOf('\n', a);
    if (e.key === 'Tab' && cur?.list) {
      e.preventDefault();
      app.editor.formatParagraphs(id, a, b, (c) => ({ list: c.list ?? undefined, level: Math.max(0, c.level + (e.shiftKey ? -1 : 1)) }));
      return true;
    }
    if (e.key === 'Enter' && !e.shiftKey && cur?.list && paraStart === paraEnd && a === b) {
      // Enter on an empty item ends the list (or steps out one level)
      e.preventDefault();
      app.editor.formatParagraphs(id, a, a, (c) => (c.level > 0 ? { list: c.list ?? undefined, level: c.level - 1 } : { list: 'NONE' }));
      return true;
    }
    if (e.key === 'Backspace' && cur?.list && a === b && a === paraStart) {
      e.preventDefault();
      app.editor.formatParagraphs(id, a, a, { list: 'NONE', level: cur.level });
      return true;
    }
    if (e.key === ' ' && a === b) {
      // "- " or "1. " typed at the start of a paragraph starts a list
      const before = chars.slice(paraStart, a);
      const kind = /^[-*•]$/.test(before) ? 'BULLET' : /^1[.)]$/.test(before) ? 'NUMBER' : null;
      if (kind && !cur?.list) {
        e.preventDefault();
        app.editor.transact('Start list', () => {
          app.editor.update(id, { characters: chars.slice(0, paraStart) + chars.slice(a) }, 'Edit text');
          app.editor.formatParagraphs(id, paraStart, paraStart, { list: kind });
        });
        t.value = chars.slice(0, paraStart) + chars.slice(a);
        setRange(paraStart, paraStart);
        return true;
      }
    }
    return false;
  };
  const toggleList = (kind: 'BULLET' | 'NUMBER') => {
    const [a, b] = range.current;
    const n = app.doc.get(id);
    if (!n) return;
    const fmts = paragraphFormats(n);
    const all = fmts && fmts.slice(paragraphAt(n.characters ?? '', a), paragraphAt(n.characters ?? '', b) + 1).every((f) => f.list === kind);
    app.editor.formatParagraphs(id, a, b, (c) => ({ list: all ? 'NONE' : kind, level: c.level }));
    requestAnimationFrame(() => {
      ref.current?.focus();
      ref.current?.setSelectionRange(a, b);
    });
  };
  return (
    <>
    {(isText || node.type === 'STICKY' || node.type === 'SHAPE_WITH_TEXT') && (
      <div className="format-bar" style={{ left: tl.x, top: tl.y - 40 }} onPointerDown={(e) => (e.preventDefault(), e.stopPropagation())}>
        <button title="Bold" onClick={() => applyStyle({ fontWeight: 700 }, 'fontWeight')}><b>B</b></button>
        <button title="Italic" onClick={() => applyStyle({ italic: true }, 'italic')}><i>I</i></button>
        <button title="Underline" onClick={() => applyStyle({ textDecoration: 'UNDERLINE' }, 'textDecoration')}><u>U</u></button>
        <button title="Strikethrough" onClick={() => applyStyle({ textDecoration: 'STRIKETHROUGH' }, 'textDecoration')}><s>S</s></button>
        <button
          title="Link"
          onClick={() => {
            const url = prompt('Link URL', 'https://');
            if (url) applyStyle({ link: url });
          }}
        >
          🔗
        </button>
        <label className="fb-color" title="Color">
          A
          <input type="color" defaultValue={colorToHex(color && color.type === 'SOLID' ? color.color : { r: 0, g: 0, b: 0, a: 1 }, false)} onChange={(e) => applyStyle({ color: parseColor(e.target.value) })} />
        </label>
        {[-1, 1].map((dir) => (
          <button
            key={dir}
            title={dir > 0 ? 'Larger' : 'Smaller'}
            onClick={() => {
              const n = app.doc.get(id);
              const [a] = range.current;
              const run = n?.textRuns?.find((r) => r.start <= a && r.end > a);
              const cur = run?.fontSize ?? n?.fontSize ?? 16;
              const steps = [8, 10, 12, 14, 16, 18, 20, 24, 28, 32, 40, 48, 56, 64, 80, 96];
              const next = dir > 0 ? (steps.find((v) => v > cur) ?? cur + 16) : ([...steps].reverse().find((v) => v < cur) ?? Math.max(1, cur - 2));
              applyStyle({ fontSize: next });
            }}
          >
            {dir > 0 ? 'A+' : 'A−'}
          </button>
        ))}
        {isText && !onPath && (
          <>
            <button title="Bulleted list" onClick={() => toggleList('BULLET')}>•≡</button>
            <button title="Numbered list" onClick={() => toggleList('NUMBER')}>1≡</button>
          </>
        )}
        <button title="Clear formatting" onClick={() => app.editor.update(id, { textRuns: undefined }, 'Clear formatting')}>⌫</button>
      </div>
    )}
    <textarea
      ref={ref}
      className={layout ? 'text-editor custom-caret' : 'text-editor'}
      style={css}
      spellCheck={false}
      defaultValue={initial}
      onInput={(e) => {
        app.editor.update(id, { characters: (e.target as HTMLTextAreaElement).value }, 'Edit text');
        syncSel();
        askReview((e.target as HTMLTextAreaElement).value);
        setSuggest(null);
      }}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (isText && listKey(e)) return;
        if ((e.key === 'Tab' || (e.key === 'Enter' && !e.shiftKey)) && !e.metaKey && !e.ctrlKey && mindMapKey(e.key, id)) {
          e.preventDefault();
          return;
        }
        if (e.key === 'Escape' || (e.key === 'Enter' && (e.metaKey || e.ctrlKey))) {
          e.preventDefault();
          app.setUI({ editingTextId: null });
          app.editor.setSelection(app.doc.has(id) ? [id] : []);
        }
      }}
      onBlur={() => {
        if (app.ui.editingTextId === id) app.setUI({ editingTextId: null });
      }}
      onPointerDown={(e) => {
        e.stopPropagation();
        if (!layout) return;
        // place the caret from our layout, not the textarea's
        e.preventDefault();
        ref.current?.focus();
        const i = localIndex(e);
        if (e.detail === 2) {
          const v = ref.current!.value;
          let s0 = i,
            s1 = i;
          while (s0 > 0 && /\w/.test(v[s0 - 1])) s0--;
          while (s1 < v.length && /\w/.test(v[s1])) s1++;
          return setRange(s0, s1);
        }
        anchor.current = e.shiftKey ? range.current[0] : i;
        setRange(anchor.current, i);
        (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
      }}
      onPointerMove={(e) => {
        if (layout && anchor.current !== null && e.buttons & 1) setRange(anchor.current, localIndex(e));
      }}
      onPointerUp={() => (anchor.current = null)}
      onSelect={syncSel}
      onKeyUp={syncSel}
    />
    {layout && (
      <div style={{ position: 'absolute', left: 0, top: 0, transformOrigin: '0 0', transform: `matrix(${m.join(',')})`, pointerEvents: 'none', zIndex: 4 }}>
        {selectionRects(layout, sel[0], sel[1]).map((r, i) => (
          <div key={i} style={{ position: 'absolute', left: r.x, top: r.y, width: r.width, height: r.height, background: 'rgba(13,153,255,.3)' }} />
        ))}
        {sel[0] === sel[1] && blink && (() => {
          const c = caretPosition(layout, sel[0]);
          return <div style={{ position: 'absolute', left: c.x, top: c.y, width: 1.5 / Math.hypot(m[0], m[1]), height: c.height, background: '#0d99ff' }} />;
        })()}
      </div>
    )}
    {review && review.text === (node.characters ?? '') && review.ranges.length > 0 && node.type !== 'CONNECTOR' && !onPath && (() => {
      const lay = layout ?? layoutNodeText(node);
      return (
        <div style={{ position: 'absolute', left: 0, top: 0, transformOrigin: '0 0', transform: `matrix(${m.join(',')})`, pointerEvents: 'none', zIndex: 5 }}>
          {review.ranges.flatMap((r, ri) =>
            selectionRects(lay, r.start, r.end).map((b, i) => (
              <div
                key={`${ri}-${i}`}
                className="review-mark"
                title={r.suggestions.length ? `Suggestions: ${r.suggestions.join(', ')}` : 'Check this'}
                style={{ left: b.x, top: b.y + b.height - 3, width: b.width, borderColor: r.color === 'BLUE' ? '#0d99ff' : r.color === 'GREEN' ? '#14ae5c' : '#f24822' }}
                onPointerDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  const p = { x: b.x * m[0] + (b.y + b.height) * m[2] + m[4], y: b.x * m[1] + (b.y + b.height) * m[3] + m[5] };
                  setSuggest({ start: r.start, end: r.end, options: r.suggestions, x: p.x, y: p.y + 4 });
                }}
              />
            )),
          )}
        </div>
      );
    })()}
    {suggest && (
      <div className="review-menu" style={{ left: suggest.x, top: suggest.y }} onPointerDown={(e) => (e.preventDefault(), e.stopPropagation())}>
        {suggest.options.length === 0 && <span className="review-none">No suggestions</span>}
        {suggest.options.map((o) => (
          <button
            key={o}
            onClick={() => {
              const text = node.characters ?? '';
              const next = text.slice(0, suggest.start) + o + text.slice(suggest.end);
              app.editor.update(id, { characters: next }, 'Apply suggestion');
              if (ref.current) {
                ref.current.value = next;
                ref.current.setSelectionRange(suggest.start + o.length, suggest.start + o.length);
              }
              setSuggest(null);
              askReview(next);
            }}
          >
            {o}
          </button>
        ))}
      </div>
    )}
    </>
  );
}
